import React, { useState, useEffect } from 'react'
import {
  Box,
  Typography,
  TextField,
  Switch,
  FormControlLabel,
  Button,
  Divider,
  Grid,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Skeleton,
  Alert,
  CircularProgress,
  Snackbar,
} from '@mui/material'
import {
  Save as SaveIcon,
  Restore as RestoreIcon,
} from '@mui/icons-material'
import { useSettings, useUpdateSettings } from '../../hooks/useSettings'

const defaultSettings = {
  siteName: 'VirtualSMS Admin Panel',
  siteDescription: 'SMS Kiralama Platform Yonetim Paneli',
  maintenanceMode: false,
  debugMode: false,
  allowRegistration: true,
  defaultCredits: 100,
  maxConcurrentRentals: 5,
  // Force Update settings
  minIosVersion: '',
  minAndroidVersion: '',
  appStoreUrl: '',
  playStoreUrl: '',
  showTipsCarousel: true,
}

const SettingsTab = ({
  title,
  description,
}) => {
  const { data: savedSettings, isLoading, error: loadError } = useSettings()
  const updateSettings = useUpdateSettings()

  const [localSettings, setLocalSettings] = useState(defaultSettings)
  const [hasChanges, setHasChanges] = useState(false)
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' })

  // Sync with saved settings when loaded
  useEffect(() => {
    if (savedSettings) {
      setLocalSettings({
        ...defaultSettings,
        ...savedSettings
      })
    }
  }, [savedSettings])

  const handleSettingChange = (key, value) => {
    setLocalSettings(prev => ({
      ...prev,
      [key]: value
    }))
    setHasChanges(true)
  }

  const handleReset = () => {
    if (savedSettings) {
      setLocalSettings({
        ...defaultSettings,
        ...savedSettings
      })
    } else {
      setLocalSettings(defaultSettings)
    }
    setHasChanges(false)
  }

  const handleSave = async () => {
    try {
      await updateSettings.mutateAsync(localSettings)
      setHasChanges(false)
    } catch (error) {
      // Error is handled by the mutation
    }
  }

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false })
  }

  if (isLoading) {
    return (
      <Box>
        <Skeleton variant="text" width="40%" height={32} sx={{ mb: 1 }} />
        <Skeleton variant="text" width="60%" height={20} sx={{ mb: 3 }} />

        {[...Array(6)].map((_, index) => (
          <Box key={index} sx={{ mb: 3 }}>
            <Skeleton variant="text" width="30%" height={24} sx={{ mb: 1 }} />
            <Skeleton variant="rectangular" height={56} />
          </Box>
        ))}
      </Box>
    )
  }

  if (loadError) {
    return (
      <Box>
        <Alert severity="error" sx={{ mb: 3 }}>
          Ayarlar yuklenirken hata olustu. Backend settings endpoint'i henuz hazir olmayabilir.
        </Alert>
        <Typography variant="body2" color="text.secondary">
          Hata detayi: {loadError.message}
        </Typography>
      </Box>
    )
  }

  return (
    <Box>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h5" sx={{ fontWeight: 600, mb: 1 }}>
          {title}
        </Typography>
        <Typography variant="body1" color="text.secondary">
          {description}
        </Typography>
      </Box>

      {/* Site Configuration */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
            Site Konfigurasyonu
          </Typography>

          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Site Adi"
                value={localSettings.siteName}
                onChange={(e) => handleSettingChange('siteName', e.target.value)}
                helperText="Admin panelinde goruntulenecek site adi"
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Site Aciklamasi"
                value={localSettings.siteDescription}
                onChange={(e) => handleSettingChange('siteDescription', e.target.value)}
                helperText="Site aciklamasi ve meta bilgiler"
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Varsayilan Kredi Miktari"
                type="number"
                value={localSettings.defaultCredits}
                onChange={(e) => handleSettingChange('defaultCredits', parseInt(e.target.value) || 0)}
                helperText="Yeni kullanicilara verilecek varsayilan kredi miktari"
                inputProps={{ min: 0, max: 1000 }}
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* System Configuration */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
            Sistem Konfigurasyonu
          </Typography>

          <List>
            <ListItem>
              <ListItemText
                primary="Bakim Modu"
                secondary="Bakim modu aktifken sadece adminler sisteme erisebilir"
              />
              <ListItemSecondaryAction>
                <Switch
                  checked={localSettings.maintenanceMode}
                  onChange={(e) => handleSettingChange('maintenanceMode', e.target.checked)}
                  color="warning"
                />
              </ListItemSecondaryAction>
            </ListItem>

            <Divider />

            <ListItem>
              <ListItemText
                primary="Debug Modu"
                secondary="Gelistirici modunu aktiflestir (sadece gelistirme ortaminda kullanin)"
              />
              <ListItemSecondaryAction>
                <Switch
                  checked={localSettings.debugMode}
                  onChange={(e) => handleSettingChange('debugMode', e.target.checked)}
                  color="primary"
                />
              </ListItemSecondaryAction>
            </ListItem>

            <Divider />

            <ListItem>
              <ListItemText
                primary="Kullanici Kaydina Izin Ver"
                secondary="Yeni kullanicilarin sisteme kayit olmasina izin ver"
              />
              <ListItemSecondaryAction>
                <Switch
                  checked={localSettings.allowRegistration}
                  onChange={(e) => handleSettingChange('allowRegistration', e.target.checked)}
                  color="success"
                />
              </ListItemSecondaryAction>
            </ListItem>

            <Divider />

            <ListItem>
              <ListItemText
                primary="Tips Carousel Göster"
                secondary="Mobil uygulamada ipuçları alanını göster/gizle"
              />
              <ListItemSecondaryAction>
                <Switch
                  checked={localSettings.showTipsCarousel}
                  onChange={(e) => handleSettingChange('showTipsCarousel', e.target.checked)}
                  color="info"
                />
              </ListItemSecondaryAction>
            </ListItem>
          </List>
        </CardContent>
      </Card>

      {/* Performance Settings */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
            Performans Ayarlari
          </Typography>

          <Grid container spacing={3}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Maksimum Eszamanli Kiralama"
                type="number"
                value={localSettings.maxConcurrentRentals}
                onChange={(e) => handleSettingChange('maxConcurrentRentals', parseInt(e.target.value) || 1)}
                helperText="Bir kullanicinin ayni anda yapabilecegi maksimum kiralama sayisi"
                inputProps={{ min: 1, max: 50 }}
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Force Update Settings */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
            Force Update Settings
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Minimum version requirements for iOS and Android apps. Leave empty to disable version check.
          </Typography>

          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Minimum iOS Version"
                value={localSettings.minIosVersion || ''}
                onChange={(e) => handleSettingChange('minIosVersion', e.target.value)}
                placeholder="e.g., 1.2.0"
                helperText="Semantic versioning format (X.Y.Z). Empty = no check"
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Minimum Android Version"
                value={localSettings.minAndroidVersion || ''}
                onChange={(e) => handleSettingChange('minAndroidVersion', e.target.value)}
                placeholder="e.g., 1.2.0"
                helperText="Semantic versioning format (X.Y.Z). Empty = no check"
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="App Store URL (iOS)"
                value={localSettings.appStoreUrl || ''}
                onChange={(e) => handleSettingChange('appStoreUrl', e.target.value)}
                placeholder="https://apps.apple.com/app/virtualsms/id123456789"
                helperText="iOS App Store link for update button"
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Play Store URL (Android)"
                value={localSettings.playStoreUrl || ''}
                onChange={(e) => handleSettingChange('playStoreUrl', e.target.value)}
                placeholder="https://play.google.com/store/apps/details?id=com.virtualsms"
                helperText="Google Play Store link for update button"
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Warning Messages */}
      {localSettings.maintenanceMode && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          <Typography variant="body2">
            <strong>Uyari:</strong> Bakim modu aktif. Sistem sadece admin kullanicilar tarafindan erisilebilir.
          </Typography>
        </Alert>
      )}

      {localSettings.debugMode && (
        <Alert severity="info" sx={{ mb: 3 }}>
          <Typography variant="body2">
            <strong>Bilgi:</strong> Debug modu aktif. Bu mod sadece gelistirme ortaminda kullanilmalidir.
          </Typography>
        </Alert>
      )}

      {hasChanges && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          <Typography variant="body2">
            Kaydedilmemis degisiklikler var.
          </Typography>
        </Alert>
      )}

      {/* Action Buttons */}
      <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
        <Button
          variant="outlined"
          startIcon={<RestoreIcon />}
          onClick={handleReset}
          disabled={updateSettings.isPending}
        >
          Sifirla
        </Button>
        <Button
          variant="contained"
          startIcon={updateSettings.isPending ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
          onClick={handleSave}
          disabled={updateSettings.isPending || !hasChanges}
        >
          {updateSettings.isPending ? 'Kaydediliyor...' : 'Ayarlari Kaydet'}
        </Button>
      </Box>

      {/* Snackbar for feedback */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        message={snackbar.message}
      />
    </Box>
  )
}

export default SettingsTab
